/**
 * Text normalization utilities to handle diacritical marks and case sensitivity
 * Matches the behavior of the database normalize_text() function
 */

/**
 * Normalize text by removing diacritical marks and converting to lowercase
 * This matches the behavior of the Supabase normalize_text() function which uses LOWER(UNACCENT())
 * 
 * @param {string} text - Text to normalize
 * @returns {string} - Normalized text
 */
export function normalizeText(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  // Remove diacritical marks and convert to lowercase
  return text
    .toLowerCase()
    .normalize('NFD') // Decompose characters with diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .trim();
}

/**
 * Normalize search term for database queries
 * 
 * @param {string} searchTerm - Search term to normalize
 * @returns {string} - Normalized search term ready for database query
 */
export function normalizeSearchTerm(searchTerm) {
  return normalizeText(searchTerm);
}

/**
 * Check if two text strings match when normalized
 * 
 * @param {string} text1 - First text
 * @param {string} text2 - Second text
 * @returns {boolean} - True if texts match when normalized
 */
export function normalizedTextMatch(text1, text2) {
  return normalizeText(text1) === normalizeText(text2);
}

/**
 * Create a search pattern for ILIKE queries with normalized text
 * 
 * @param {string} searchTerm - Search term to create pattern for
 * @returns {string} - Search pattern with % wildcards
 */
export function createSearchPattern(searchTerm) {
  const normalized = normalizeSearchTerm(searchTerm);
  return `%${normalized}%`;
}
